<template>
  <section
    v-if="data.parts"
    ref="section"
    class="cn-rich-header"
    :class="{
      'cn-rich-header--multipart': data.parts.length > 1,
    }"
  >
    <div class="cn-rich-header__block">
      <div class="container">
        <div class="cn-rich-header__content">
          <div v-if="data.parts.length > 1" class="cn-rich-header__pagination">
            <div class="pagination-bullet1">
              <div class="pagination-progress"></div>
            </div>
            <div class="pagination-bullet2">
              <div class="pagination-progress"></div>
            </div>
          </div>
          <div class="cn-rich-header__main">
            <div class="cn-rich-header__text">
              <div class="cn-rich-header__text-wrapper">
                <div class="cn-rich-header__text-content">
                  <div class="text1">
                    <h2 v-if="data.title" class="cn-rich-header__title">
                      {{ data.title }}
                    </h2>
                    <div v-if="data.parts[0]">
                      <p class="cn-rich-header__description">
                        {{ data.parts[0].text }}
                      </p>
                    </div>
                  </div>
                  <div v-if="data.parts[1]" class="text2">
                    <p class="cn-rich-header__description">
                      {{ data.parts[1].text }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="cn-rich-header__image">
              <div class="cn-rich-header__image-wrapper">
                <CdnImg
                  v-if="data.parts[0]"
                  :src="data.parts[0].image.path"
                  class="image1"
                  format="webp"
                  loading="lazy"
                  :width="590"
                  :height="590"
                />
                <CdnImg
                  v-if="data.parts[1]"
                  :src="data.parts[1].image.path"
                  class="image2"
                  format="webp"
                  loading="lazy"
                  :width="590"
                  :height="590"
                />
                <UiParallelogram
                  color="white"
                  class="cn-rich-header__parallelogram-1"
                />
                <UiParallelogram
                  color="white"
                  class="cn-rich-header__parallelogram-2"
                />
                <UiParallelogram
                  color="white"
                  class="cn-rich-header__parallelogram-3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import { ref, onMounted } from 'vue';
import UiParallelogram from '@/components/ui/UiParallelogram';

// TODO: fix type
interface Props {
  data: any;
}
const props = defineProps<Props>();

const { $gsap, $ScrollTrigger } = useNuxtApp();
const section = ref(null);

const tl = ref();

onMounted(() => {
  if (!props.data.parts || props.data.parts.length < 2) return;
  tl.value = $gsap.timeline({
    scrollTrigger: {
      trigger: section.value,
      start: 'center+=500 center',
      toggleActions: 'restart none none reverse',
    },
    defaults: { ease: 'none' },
  });

  tl.value
    .fromTo(
      section.value.querySelector('.image1'),
      { opacity: 1, y: 0 },
      { opacity: 0, y: -20 },
    )
    .fromTo(
      section.value.querySelector('.image2'),
      { opacity: 0, scaleX: 0.95, scaleY: 0.95, y: 20 },
      { opacity: 1, scaleX: 1, scaleY: 1, y: 0 },
    )

    .fromTo(
      section.value.querySelector('.text1'),
      { opacity: 1, y: 0 },
      { opacity: 0, y: -20 },
      0,
    )
    .fromTo(
      section.value.querySelector('.text2'),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0 },
      0.5,
    );

  // Pagination

  $gsap.fromTo(
    section.value.querySelector('.pagination-bullet1 .pagination-progress'),
    { height: 0 },
    {
      height: '100%',
      scrollTrigger: {
        trigger: section.value,
        start: 'center center',
        end: '+=500',
        scrub: true,
        toggleActions: 'restart none none reverse',
      },
    },
  );

  $gsap.fromTo(
    section.value.querySelector('.pagination-bullet2 .pagination-progress'),
    { height: 0 },
    {
      height: '100%',
      scrollTrigger: {
        trigger: section.value,
        start: 'center+=500 center',
        end: '+=500',
        scrub: true,
        toggleActions: 'restart none none reverse',
      },
    },
  );

  $ScrollTrigger.create({
    trigger: section.value,
    start: 'center center',
    end: '+=1000',
    pin: true,
    anticipatePin: 1,
  });
});
</script>

<style lang="scss">
.cn-rich-header {
  --ab-canyon-rich-header-block-color-bg: var(--ab-canyon-color-accent-2);
  --ab-canyon-rich-header-title-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-rich-header-description-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-rich-header-pagination-bullets-color-bg: var(
    --ab-canyon-color-light
  );
  --ab-canyon-rich-header-pagination-progress-color-bg: var(
    --ab-canyon-color-light
  );

  padding-top: var(--size-7xl);
  overflow: hidden;

  &__block {
    background-color: var(--ab-canyon-rich-header-block-color-bg);
    align-items: center;
    height: 100%;
    display: flex;

    > .container {
      position: relative;
    }
  }
  &__content {
    align-items: center;

    display: flex;
  }
  &__title {
    color: var(--ab-canyon-rich-header-title-color-text);
    font-size: 1.125rem;
    line-height: 1.75rem;
    font-weight: 700;
    text-transform: uppercase;

    @media (min-width: 768px) {
      font-size: 1.875rem;
      line-height: 2.25rem;
    }
    @media (min-width: 1600px) {
      font-size: 2.25rem;
      line-height: 2.5rem;
    }
  }

  &__description {
    color: var(--ab-canyon-rich-header-description-color-text);
    padding-top: var(--size-base);
    font-size: 0.875rem;
    line-height: 1.25rem;
    @media (min-width: 640px) {
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }

  &__pagination {
    row-gap: calc(var(--size-base) / 2);
    flex-direction: column;
    display: flex;
    .pagination-bullet1,
    .pagination-bullet2 {
      background-color: color-mix(
        in srgb,
        var(--ab-canyon-rich-header-pagination-bullets-color-bg),
        transparent 70%
      );

      width: calc(var(--size-base) / 4);
      height: 90px;
    }
    .pagination-progress {
      background-color: var(
        --ab-canyon-rich-header-pagination-progress-color-bg
      );
      width: 100%;
      height: 0px;
    }
  }
  &__main {
    column-gap: var(--size-xl);
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    display: flex;
    z-index: 10;
    position: relative;
    @media (min-width: 1280px) {
      flex-wrap: nowrap;
    }
  }
  &__text {
    max-width: calc(42 * var(--size-base));
    width: 100%;
    display: flex;
    @media (min-width: 1280px) {
      max-width: none;
      width: 50%;
    }

    .text2 {
      top: 0px;
      left: 0px;
      position: absolute;
    }
  }
  &__text-wrapper {
    padding-top: var(--size-3xl);
    padding-bottom: var(--size-3xl);
    @media (min-width: 656px) {
      padding-top: var(--size-5xl);
      padding-bottom: var(--size-5xl);
    }
    @media (min-width: 1280px) {
      padding-top: var(--size-7xl);
      padding-bottom: var(--size-7xl);
    }
  }
  &__text-content {
    position: relative;
  }
  &__image {
    align-items: center;
    width: 100%;
    display: flex;
    order: -9999;
    @media (min-width: 1280px) {
      width: 50%;
      order: 0;
    }

    .image1 {
      width: 100%;
      z-index: 10;
      position: relative;
    }
    .image2 {
      opacity: 0;
      width: 100%;
      z-index: 10;
      top: 0px;
      position: absolute;
    }
  }
  &__image-wrapper {
    max-width: calc(24 * var(--size-base));
    width: 100%;
    margin-top: calc(var(--size-7xl) * -1);
    margin-bottom: calc(var(--size-4xl) * -1);
    margin-left: auto;
    margin-right: auto;
    position: relative;
    @media (min-width: 1280px) {
      max-width: none;
      margin-top: -10%;
    }
  }
  &__parallelogram-1 {
    border-width: 3px;
    width: 40%;
    height: 60%;
    left: 45%;
    bottom: 50%;
    position: absolute;
    @media (min-width: 1280px) {
      border-width: 4px;
      width: 45%;
      left: 50%;
      bottom: 40%;
    }
  }
  &__parallelogram-2 {
    border-width: 2px;
    width: 8%;
    height: 13%;
    top: 50px;
    right: -20px;
    position: absolute;
    @media (min-width: 1280px) {
      border-width: 3px;
      top: 100px;
      right: -80px;
    }
  }
  &__parallelogram-3 {
    border-width: 2px;
    width: 8%;
    height: 13%;
    top: 55%;
    right: -20px;
    position: absolute;
    @media (min-width: 1280px) {
      border-width: 3px;
      top: auto;
      right: -80px;
      bottom: 20%;
    }
  }
  &--multipart {
    .cn-rich-header {
      &__main {
        padding-left: var(--size-3xl);
        @media (min-width: 1280px) {
          padding-left: calc(8 * var(--size-base));
        }
      }
    }
  }
}
</style>
